import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Megaphone, Mail, Globe, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { FinanceLayout, Pill, Empty, money } from './FinanceLayout';
import { useAuth } from '../../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', contact_name: '', contact_email: '', phone: '', company: '',
  billing_address: '', country: '', tax_id: '', notes: '',
};

export default function FinanceAdvertisersPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [advertisers, setAdvertisers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API}/admin/finance/advertisers`, { headers }),
        axios.get(`${API}/admin/finance/campaigns`, { headers }),
      ]);
      setAdvertisers(a.data);
      setCampaigns(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/finance/advertisers/${editingId}`, form, { headers });
        toast.success('Advertiser updated');
      } else {
        await axios.post(`${API}/admin/finance/advertisers`, form, { headers });
        toast.success('Advertiser added');
      }
      setForm(EMPTY); setEditingId(null); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const edit = (a) => {
    setForm({ ...EMPTY, ...a });
    setEditingId(a.advertiser_id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this advertiser? (only works if no campaigns attached)')) return;
    try {
      await axios.delete(`${API}/admin/finance/advertisers/${id}`, { headers });
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const campaignCount = (advertiserId) => campaigns.filter(c => c.advertiser_id === advertiserId).length;
  const campaignRevenue = (advertiserId) => campaigns
    .filter(c => c.advertiser_id === advertiserId && c.status === 'live')
    .reduce((sum, c) => sum + Number(c.flat_fee_usd || 0), 0);

  const actions = (
    <button
      onClick={() => { setEditingId(null); setForm(EMPTY); setShowForm(!showForm); }}
      data-testid="toggle-advertiser-form"
      className="bg-white text-blue-700 px-4 py-2 rounded-full font-bold shadow-lg hover:scale-105 transition flex items-center gap-2 text-sm"
    >
      <Plus className="w-4 h-4" /> {showForm ? 'Close' : 'New advertiser'}
    </button>
  );

  return (
    <FinanceLayout title="Advertisers" subtitle={`${advertisers.length} total`} actions={actions}>
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="advertiser-form">
          <h3 className="font-black text-slate-900">{editingId ? 'Edit advertiser' : 'New advertiser'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <L label="Organization name" req><I value={form.name} onChange={v => setForm({ ...form, name: v })} testid="adv-name" /></L>
            <L label="Contact name"><I value={form.contact_name} onChange={v => setForm({ ...form, contact_name: v })} testid="adv-contact" /></L>
            <L label="Contact email"><I type="email" value={form.contact_email} onChange={v => setForm({ ...form, contact_email: v })} testid="adv-email" /></L>
            <L label="Phone"><I value={form.phone} onChange={v => setForm({ ...form, phone: v })} testid="adv-phone" /></L>
            <L label="Company"><I value={form.company} onChange={v => setForm({ ...form, company: v })} testid="adv-company" /></L>
            <L label="Country"><I value={form.country} onChange={v => setForm({ ...form, country: v })} testid="adv-country" /></L>
            <L label="Tax ID / VAT"><I value={form.tax_id} onChange={v => setForm({ ...form, tax_id: v })} testid="adv-tax" /></L>
            <L label="Billing address" spans={2}><I value={form.billing_address} onChange={v => setForm({ ...form, billing_address: v })} testid="adv-address" /></L>
            <L label="Notes" spans={2}><I value={form.notes} onChange={v => setForm({ ...form, notes: v })} testid="adv-notes" /></L>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY); }} className="px-4 py-2 rounded-full bg-slate-100 font-bold text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="adv-submit" className="px-5 py-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold text-sm disabled:opacity-50">
              {busy ? 'Saving…' : editingId ? 'Update' : 'Add advertiser'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-500 border-t-transparent" /></div>
      ) : advertisers.length === 0 ? (
        <Empty text="No advertisers yet. Add your first advertiser to start tracking ad revenue, then create campaigns for them under Ad Campaigns." />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <Th>Advertiser</Th>
                <Th>Contact</Th>
                <Th>Country</Th>
                <Th align="right">Campaigns</Th>
                <Th align="right">Live revenue</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {advertisers.map(a => (
                <tr key={a.advertiser_id} className="hover:bg-slate-50" data-testid={`adv-row-${a.advertiser_id}`}>
                  <Td><div className="font-bold text-slate-900">{a.name}</div>{a.company && <div className="text-xs text-slate-500">{a.company}</div>}</Td>
                  <Td>{a.contact_name || '—'}<div className="text-xs text-slate-500"><Mail className="w-3 h-3 inline mr-1" />{a.contact_email || '—'}</div></Td>
                  <Td>{a.country ? <><Globe className="w-3 h-3 inline mr-1" />{a.country}</> : '—'}</Td>
                  <Td align="right"><Pill tone={campaignCount(a.advertiser_id) > 0 ? 'blue' : 'slate'}>{campaignCount(a.advertiser_id)}</Pill></Td>
                  <Td align="right" bold>{money(campaignRevenue(a.advertiser_id))}</Td>
                  <Td align="right">
                    <button onClick={() => edit(a)} data-testid={`edit-adv-${a.advertiser_id}`} className="text-slate-500 hover:text-blue-600 p-1.5"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => del(a.advertiser_id)} data-testid={`del-adv-${a.advertiser_id}`} className="text-slate-500 hover:text-rose-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Campaigns section */}
      <CampaignsSection advertisers={advertisers} onChange={load} />
    </FinanceLayout>
  );
}

function CampaignsSection({ advertisers, onChange }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [campaigns, setCampaigns] = useState([]);
  const [tours, setTours] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ advertiser_id: '', name: '', placement_type: 'pre_roll', flat_fee_usd: '', start_date: '', end_date: '', status: 'draft', notes: '', tour_ids: [] });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, t] = await Promise.all([
      axios.get(`${API}/admin/finance/campaigns`, { headers }),
      axios.get(`${API}/admin/tours`, { headers }),
    ]);
    setCampaigns(c.data);
    setTours(t.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, flat_fee_usd: Number(form.flat_fee_usd || 0) };
      if (editingId) {
        await axios.put(`${API}/admin/finance/campaigns/${editingId}`, body, { headers });
        toast.success('Campaign updated');
      } else {
        await axios.post(`${API}/admin/finance/campaigns`, body, { headers });
        toast.success('Campaign added');
      }
      setShowForm(false); setEditingId(null);
      setForm({ advertiser_id: '', name: '', placement_type: 'pre_roll', flat_fee_usd: '', start_date: '', end_date: '', status: 'draft', notes: '', tour_ids: [] });
      load(); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    await axios.delete(`${API}/admin/finance/campaigns/${id}`, { headers });
    toast.success('Deleted'); load(); onChange();
  };

  if (advertisers.length === 0) return null;

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-black text-slate-900 flex items-center gap-2"><Briefcase className="w-5 h-5" /> Ad campaigns</h2>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); }} data-testid="toggle-campaign-form" className="text-sm font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <Plus className="w-4 h-4" /> {showForm ? 'Close' : 'New campaign'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <L label="Advertiser" req>
              <select value={form.advertiser_id} onChange={e => setForm({ ...form, advertiser_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" required data-testid="cmp-advertiser">
                <option value="">— select —</option>
                {advertisers.map(a => <option key={a.advertiser_id} value={a.advertiser_id}>{a.name}</option>)}
              </select>
            </L>
            <L label="Campaign name" req><I value={form.name} onChange={v => setForm({ ...form, name: v })} testid="cmp-name" /></L>
            <L label="Placement">
              <select value={form.placement_type} onChange={e => setForm({ ...form, placement_type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="cmp-placement">
                <option value="pre_roll">Pre-roll</option>
                <option value="mid_roll">Mid-roll</option>
                <option value="post_roll">Post-roll</option>
                <option value="sponsor_mention">Sponsor mention</option>
              </select>
            </L>
            <L label="Flat fee (USD)" req><I type="number" value={form.flat_fee_usd} onChange={v => setForm({ ...form, flat_fee_usd: v })} testid="cmp-fee" /></L>
            <L label="Start date"><I type="date" value={form.start_date} onChange={v => setForm({ ...form, start_date: v })} testid="cmp-start" /></L>
            <L label="End date"><I type="date" value={form.end_date} onChange={v => setForm({ ...form, end_date: v })} testid="cmp-end" /></L>
            <L label="Status">
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="cmp-status">
                <option value="draft">Draft</option>
                <option value="live">Live</option>
                <option value="completed">Completed</option>
                <option value="canceled">Canceled</option>
              </select>
            </L>
            <L label="Tours (hold ⌘/Ctrl to select multiple)" spans={2}>
              <select multiple value={form.tour_ids} onChange={e => setForm({ ...form, tour_ids: Array.from(e.target.selectedOptions).map(o => o.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" size={Math.min(5, tours.length || 1)} data-testid="cmp-tours">
                {tours.map(t => <option key={t.tour_id} value={t.tour_id}>{t.title} ({t.city})</option>)}
              </select>
            </L>
          </div>
          <div className="flex justify-end gap-2">
            <button type="submit" disabled={busy} data-testid="cmp-submit" className="px-5 py-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold text-sm disabled:opacity-50">
              {busy ? 'Saving…' : editingId ? 'Update' : 'Add campaign'}
            </button>
          </div>
        </form>
      )}

      {campaigns.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">No campaigns yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <Th>Campaign</Th><Th>Advertiser</Th><Th>Placement</Th><Th>Dates</Th>
                <Th align="right">Fee</Th><Th>Status</Th><Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {campaigns.map(c => (
                <tr key={c.campaign_id} className="hover:bg-slate-50" data-testid={`cmp-row-${c.campaign_id}`}>
                  <Td bold>{c.name}</Td>
                  <Td>{c.advertiser_name}</Td>
                  <Td><Pill tone="slate">{c.placement_type}</Pill></Td>
                  <Td>{c.start_date || '—'} → {c.end_date || '—'}</Td>
                  <Td align="right" bold>{money(c.flat_fee_usd)}</Td>
                  <Td><Pill tone={c.status === 'live' ? 'emerald' : c.status === 'draft' ? 'slate' : c.status === 'canceled' ? 'rose' : 'blue'}>{c.status}</Pill></Td>
                  <Td align="right"><button onClick={() => del(c.campaign_id)} data-testid={`del-cmp-${c.campaign_id}`} className="text-slate-500 hover:text-rose-600 p-1.5"><Trash2 className="w-4 h-4" /></button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// shared micro-components
const L = ({ label, req, children, spans }) => (
  <label className={`block ${spans === 2 ? 'md:col-span-2' : ''}`}>
    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{label}{req && <span className="text-rose-500"> *</span>}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const I = ({ value, onChange, type = 'text', testid }) => (
  <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid={testid} />
);
const Th = ({ children, align = 'left' }) => <th className={`px-4 py-3 text-${align} font-bold`}>{children}</th>;
const Td = ({ children, align = 'left', bold }) => <td className={`px-4 py-2.5 text-${align} ${bold ? 'font-bold text-slate-900' : ''}`}>{children}</td>;
